import type { TestRunResult } from './types';

/**
 * The v2 orchestrator injects a synthetic boot-smoke spec (`__boot_smoke.spec.ts`)
 * into a test run to catch boot crashes, then strips it from its own accounting
 * (`bootSmokeMerge.stripBootSmoke` in @gotti/sandbox-ai-v2) so the synthetic
 * no-op test never inflates the score. The browser runner, however, runs the
 * spec like any other and broadcasts the RAW result to in-page observers (the
 * Tests panel + the e2e eval gate) — which then over-count by that one synthetic
 * test (e.g. 49 real cases reported as 50).
 *
 * `stripSyntheticSuites` mirrors the orchestrator's strip on the broadcast copy
 * so in-page observers report the SAME shipped count the orchestrator does. It
 * is applied ONLY to the `window.postMessage` payload — the value returned to
 * the agent (→ postTestResults → backend/checkpoint) stays raw, so the
 * orchestrator's own strip + boot-crash promotion are untouched and checkpoint
 * replay is byte-identical.
 */
export const SYNTHETIC_SPEC_PATHS: ReadonlySet<string> = new Set(['__boot_smoke.spec.ts']);

const normPath = (path: string): string => path.replace(/^\/+/, '');

/**
 * True when `path` (a suite's `.file`/`.name`, or a bare file path like
 * `RunTestsResult.fileResults[].path`) names a synthetic spec the pipeline
 * injects for its own bookkeeping (currently just the boot-smoke spec) —
 * never a real user/generated test. Shared by `stripSyntheticSuites` and any
 * OTHER counter that must agree with it (e.g. the live TEST_PROGRESS
 * rollup in agentStore.ts), so the synthetic-name list + its normalisation
 * live in exactly one place.
 */
export function isSyntheticSpecPath(
  path: string,
  synthetic: ReadonlySet<string> = SYNTHETIC_SPEC_PATHS,
): boolean {
  return synthetic.has(normPath(path));
}

export function stripSyntheticSuites(
  result: TestRunResult,
  synthetic: ReadonlySet<string> = SYNTHETIC_SPEC_PATHS,
): TestRunResult {
  const isSynthetic = (s: TestRunResult['suites'][number]): boolean =>
    isSyntheticSpecPath(s.file ?? s.name, synthetic);
  const removed = result.suites.filter(isSynthetic);
  if (removed.length === 0) return result; // nothing synthetic ⇒ unchanged reference

  let tests = 0, passed = 0, failed = 0;
  for (const s of removed) {
    tests += s.tests.length;
    passed += s.passed;
    failed += s.failed;
  }
  return {
    ...result,
    suites: result.suites.filter((s: TestRunResult['suites'][number]) => !isSynthetic(s)),
    totalTests: Math.max(0, result.totalTests - tests),
    totalPassed: Math.max(0, result.totalPassed - passed),
    totalFailed: Math.max(0, result.totalFailed - failed),
  };
}
